import type { ChangeEvent } from "react"
import React, { useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import { useMutation, useQuery, useQueryClient } from "react-query"
import { useLocation, useParams } from "react-router-dom"

import { Box, Grid, Skeleton } from "@mui/material"
import type { InputChangeEvent } from "@synapse-analytics/synapse-ui"
import {
  DateRangePicker,
  InputText,
  NotificationUtils,
  RadioButton,
  RadioButtonGroup,
  Typography,
} from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"
import moment from "moment"
import queryString from "query-string"

import { CSVIcon, JSONIcon } from "../../assets/SvgIcons"
import { KonanAPI } from "../../services/KonanAPI"
import type { CreateExportJobRequestProps } from "../../types/custom/dataFiles"
import { ExportJobCreateRequest } from "../../types/generated/api/ExportJobCreateRequest"
import type { ExportJobRetrieve } from "../../types/generated/api/ExportJobRetrieve"
import type { PaginatedListModelOutputsList } from "../../types/generated/api/PaginatedListModelOutputsList"
import type { PaginatedListPredictionsList } from "../../types/generated/api/PaginatedListPredictionsList"
import type { StandardErrorType } from "../../utils/ErrorHandling"
import { KonanTimeHelper } from "../../utils/genericHelpers"
import { BaseSimpleDialog } from "./BaseSimpleDialog"

import styles from "./ExportDataDialog.module.scss"

interface Props {
  onClose: () => void
  jobType: ExportJobCreateRequest.type
  model?: string
  simulation_job?: string
}

/** ExportDataDialog container that wraps the date picker for choosing the predictions time range
 *  as well as the name of the Exported data file name
 *
 * @param {function} onClose Callback function called when the dialog is closed.
 * @param {ExportJobCreateRequest.type} jobType The type of export job.
 * @param {string} [model] The model associated with the export job.
 * @param {string} [simulation_job] The simulation job associated with the export job.
 * @returns {React.ReactElement}
 */
export function ExportDataDialog(props: Readonly<Props>): React.ReactElement {
  const { onClose, jobType, model, simulation_job } = props
  const { t } = useTranslation("dialogs")
  const { id: projectId } = useParams<{ id: string }>()

  const queryClient = useQueryClient()
  const konanTime = new KonanTimeHelper()
  const location = useLocation()

  // fetches query search params from URL
  const queryParams = useMemo(() => queryString.parse(location.search), [location.search])

  const [startDate, setStartDate] = useState<moment.Moment | null>(
    queryParams.startDate && typeof queryParams.startDate === "string"
      ? moment(queryParams.startDate)
      : konanTime.adjustDate(moment().subtract(30, "days"), "start"),
  )
  const [endDate, setEndDate] = useState<moment.Moment | null>(
    queryParams.endDate && typeof queryParams.endDate === "string"
      ? moment(queryParams.endDate)
      : konanTime.adjustDate(moment(), "end"),
  )

  const fileFormats = [
    {
      title: t("ExportDataDialog.fileFormats.csv.title"),
      subtitle: t("ExportDataDialog.fileFormats.csv.subtitle"),
      value: ExportJobCreateRequest.data_type.CSV,
      icon: <CSVIcon />,
    },
    {
      title: t("ExportDataDialog.fileFormats.json.title"),
      subtitle: t("ExportDataDialog.fileFormats.json.subtitle"),
      value: ExportJobCreateRequest.data_type.JSON,
      icon: <JSONIcon />,
    },
  ]

  const [jobName, setJobName] = useState<string>("")
  const [exportFormat, setExportFormat] = useState<ExportJobCreateRequest.data_type>(
    ExportJobCreateRequest.data_type.CSV,
  )

  // Fetching metadata selectively
  const { isLoading: isRequestLogsLoading, data: requestLogData } = useQuery<
    AxiosResponse<PaginatedListModelOutputsList | PaginatedListPredictionsList>
  >(
    ["export-request-log", startDate, endDate, model],
    () =>
      model
        ? KonanAPI.fetchModelRequestLogData(
            startDate?.toISOString() as string,
            endDate?.toISOString() as string,
            model,
            1,
            1,
          )
        : KonanAPI.fetchProjectRequestLogData(
            startDate?.toISOString() as string,
            endDate?.toISOString() as string,
            projectId as string,
            1,
            1,
          ),
    { keepPreviousData: true, enabled: simulation_job === undefined },
  )

  const createExportJobMutation = useMutation<
    AxiosResponse<ExportJobRetrieve>,
    AxiosError<StandardErrorType>,
    CreateExportJobRequestProps
  >(KonanAPI.createExportJob, {
    onSuccess: () => {
      queryClient.invalidateQueries(["data-files", projectId])

      NotificationUtils.toast(t("ExportDataDialog.messages.exportStarted"), {
        severity: "success",
      })
    },
  })

  const onAcceptClicked = async (): Promise<void> => {
    await createExportJobMutation.mutateAsync({
      predictions_end_time: simulation_job ? undefined : (endDate?.toISOString() as string),
      predictions_start_time: simulation_job ? undefined : (startDate?.toISOString() as string),
      type: jobType,
      name: jobName,
      project_uuid: projectId as string,
      model: model,
      simulation_job: simulation_job,
      data_type: exportFormat,
    })

    onClose()
  }

  return (
    <BaseSimpleDialog
      open
      // TODO:: make name nullable
      name={""}
      onAccept={async () => onAcceptClicked()}
      onClose={onClose}
      mode={"export-table"}
      isLoading={createExportJobMutation.isLoading}
      disableMainBtn={jobName === "" || (!isRequestLogsLoading && requestLogData?.data.count === 0)}
    >
      <Grid item>
        <Typography
          variant="label-bold"
          tooltip={t("ExportDataDialog.labels.fileNameTooltip")}
          style={{ marginBottom: "8px" }}
        >
          {t("ExportDataDialog.labels.fileName")}
        </Typography>
        <InputText
          id="Name"
          value={jobName}
          placeholder={t("ExportDataDialog.labels.name")}
          fullWidth
          handleChange={(e: InputChangeEvent) => setJobName(e.target.value as string)}
          description={t("ExportDataDialog.labels.required")}
        />
      </Grid>

      {!simulation_job && (
        <Grid item xs={12} className="data-popper-modifier" mt={2}>
          <Typography variant="label-bold" gutterBottom>
            {t("ExportDataDialog.labels.dataRange")}
          </Typography>
          <Typography variant="span-regular" gutterBottom>
            {t("ExportDataDialog.labels.dataRangeDescription")}
          </Typography>
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={(startDate: moment.Moment | null) =>
              setStartDate(konanTime.adjustDate(startDate, "start"))
            }
            onEndDateChange={(endDate: moment.Moment | null) => setEndDate(konanTime.adjustDate(endDate, "end"))}
            disableFuture
          />
          {isRequestLogsLoading ? (
            <Skeleton animation="wave" width="30%" height="60%" style={{ marginBottom: "8px" }} />
          ) : (
            <Grid item mt={1}>
              <Typography variant="span-regular" gutterBottom>
                {requestLogData?.data.count} {t("ExportDataDialog.labels.requests")}
              </Typography>
            </Grid>
          )}
        </Grid>
      )}
      <Grid item mt={2}>
        <Typography variant="label-bold" gutterBottom>
          {t("ExportDataDialog.labels.fileFormat")}
        </Typography>
        <RadioButtonGroup
          onChange={(e: ChangeEvent<HTMLInputElement>): void =>
            setExportFormat(e.target.value as ExportJobCreateRequest.data_type)
          }
          hideDescription
        >
          <Grid container spacing={1}>
            {fileFormats.map((format) => (
              <Grid
                key={format.value}
                item
                xs={12}
                sx={{
                  cursor: "pointer",
                }}
                onClick={() => setExportFormat(format.value)}
              >
                <Box
                  className={`${styles.option} ${format.value === exportFormat ? styles.selectedOption : styles.unselectedOption}`}
                >
                  <RadioButton value={format.value} checked={exportFormat === format.value} />
                  {format.icon}
                  <Box>
                    <Typography variant="label-bold">{format.title}</Typography>
                    <Typography variant="span-regular">{format.subtitle}</Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </RadioButtonGroup>
      </Grid>
    </BaseSimpleDialog>
  )
}
